import { ParameterMetadata, ParameterType, PrimitiveType } from '../api/api';
import { ConstExpressionModel } from '../views/FlowDesigner/Models/ConstExpressionModel';
import { ExpressionTypes } from '../views/FlowDesigner/Models/ExpressionTypes';
import { VarExpressionModel } from '../views/FlowDesigner/Models/VarExpressionModel';

export const SIMPLE_PRIMITIVE_TYPES = new Set([
    PrimitiveType.String,
    PrimitiveType.Integer,
    PrimitiveType.Decimal,
    PrimitiveType.Boolean,
    PrimitiveType.Guid,
    PrimitiveType.DateTime,
    PrimitiveType.TimeSpan,
]);

const CONST_VALUE_TYPE_PRIMITIVE_TYPES = new Set([
    PrimitiveType.Guid,
    PrimitiveType.DateTime,
    PrimitiveType.TimeSpan,
]);

/**
 * Applies default parameter values from metadata to a model object.
 * 
 * Special handling for Expression parameters with expectedExpressionIsAssignable:
 * - These are wrapped in a VarExpressionModel structure: { Type: 'Var', Value: [defaultValue] }
 * - This allows them to reference variables in the variable scope at runtime
 * 
 * All other parameters with defaultValue are assigned directly.
 * 
 * @param base - The base model object to apply defaults to
 * @param parameters - Array of parameter metadata containing default values
 * @returns The model with default values applied
 */
function parseDefaultValue(value: unknown): unknown {
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
}

function isExpressionDefault(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && typeof (value as Record<string, unknown>).Type === 'string';
}

export function applyObjectDefaultParameters<T extends Record<string, any>>(
    base: T,
    parameters: ParameterMetadata[] | undefined
): T {
    if (!parameters || parameters.length === 0) {
        return base;
    }

    return parameters.reduce((model, param) => {
        if (param.defaultValue !== undefined && param.defaultValue !== null) {
            const defaultValue = parseDefaultValue(param.defaultValue);

            // Metadata can supply a complete serialized expression, for example a
            // StringTemplate. Preserve its expression shape rather than turning its
            // JSON source into a literal Const string.
            if (param.type === ParameterType.Expression && isExpressionDefault(defaultValue)) {
                return { ...model, [param.name]: defaultValue };
            }

            // Handle Expression parameters with expectedExpressionIsAssignable specially
            // These should be wrapped in a VarExpressionModel referencing a variable
            if (param.type === ParameterType.Expression && param.expectedExpressionIsAssignable) {
                return {
                    ...model,
                    [param.name]: { Type: ExpressionTypes.Var, Value: [defaultValue] } as VarExpressionModel
                };
            }
            if (param.type === ParameterType.Expression && SIMPLE_PRIMITIVE_TYPES.has(param.expectedType?.type as PrimitiveType)) {
                const expression = { Type: ExpressionTypes.Const, Value: defaultValue } as ConstExpressionModel;
                if (CONST_VALUE_TYPE_PRIMITIVE_TYPES.has(param.expectedType?.type as PrimitiveType)) {
                    expression.ValueType = param.expectedType?.type as PrimitiveType;
                }
                return {
                    ...model,
                    [param.name]: expression
                };
            }
            // Parse JSON string default values (e.g. "[]", "[10, 20, 30, 50]") into their
            // actual types. Plain strings that are not valid JSON are kept as-is.
            return { ...model, [param.name]: defaultValue };
        }
        return model;
    }, base);
}
